export const LEGACY_CATEGORY_ORDER = [
  'Kazan\u0131m Denemeleri',
  'Tekrar F\u00f6y\u00fc',
  'Yaz\u0131l\u0131 Haz\u0131rl\u0131k F\u00f6y\u00fc',
];

export interface GroupableBookSection {
  section_label: string;
  section_display_label?: string | null;
  section_category?: string | null;
  section_topic?: string | null;
  topic_name?: string | null;
  node_id?: number | null;
}

/** Keep the stable section_label for routing/submission and use the authored
 * title only where text is rendered. Legacy payloads simply fall back. */
export function displayBookSectionLabel(section: GroupableBookSection): string {
  return section.section_display_label || section.section_label;
}

export interface BookSectionGroup<T> {
  key: string;
  stem: string;
  category: string;
  members: T[];
}

/**
 * Group the already-ordered API section stream without deriving a second
 * ordering from human-facing labels. The API emits DFS node order followed by
 * book_node_tests.sort_order; title suffixes can describe pages, DAF numbers,
 * or outcome numbers and are not placement keys.
 */
export function groupBookSections<T extends GroupableBookSection>(sections: T[]): BookSectionGroup<T>[] {
  const map = new Map<string, { stem: string; members: T[]; category: string; order: number }>();
  const romanStrip = /\s*[-\u2013]\s*[IVX]+\s*$/;

  sections.forEach((section, index) => {
    const stem = section.section_topic
      || section.section_category
      || (section.topic_name || section.section_label).replace(romanStrip, '').trim()
      || section.section_label;
    const category = section.section_topic ? (section.section_category ?? '') : '';
    // Display text is not identity: two source nodes may legitimately share a
    // title. New-model payloads carry the owning node ID, which keeps those
    // nodes as separate authored groups without changing either visible name.
    const key = section.section_topic && section.node_id != null
      ? `node:${section.node_id}`
      : `label:${category}\u0000${stem}`;
    if (!map.has(key)) map.set(key, { stem, members: [], category, order: index });
    map.get(key)!.members.push(section);
  });

  const entries = Array.from(map.entries());
  entries.sort(([, leftGroup], [, rightGroup]) => {
    const leftCanonical = LEGACY_CATEGORY_ORDER.indexOf(leftGroup.stem);
    const rightCanonical = LEGACY_CATEGORY_ORDER.indexOf(rightGroup.stem);
    if (leftCanonical >= 0 && rightCanonical >= 0) return leftCanonical - rightCanonical;
    if (leftCanonical >= 0) return -1;
    if (rightCanonical >= 0) return 1;
    return leftGroup.order - rightGroup.order;
  });

  return entries.map(([key, group]) => ({
    key,
    stem: group.stem,
    category: group.category,
    members: group.members,
  }));
}
